import { IconButton, IconButtonSize, Like, LikeSolid, Dislike, DislikeSolid } from '@soteria/design-system-ve';
import { useCallback } from 'react';

import * as styles from './LikeDislikeButtons.css';

export interface LikeDislikeButtonsProps {
  /** Current value: true for like, false for dislike, null for no selection */
  value: boolean | null;
  /** Callback when value changes */
  onChange: (value: boolean) => void;
  /** Whether the buttons are disabled */
  disabled?: boolean;
  /** Size of the icon buttons */
  size?: IconButtonSize;
  /** Additional CSS class name */
  className?: string;
  /** Name for the button group (for accessibility) */
  name: string;
}

/**
 * A pair of Like/Dislike icon buttons where only one can be selected at a time.
 * Pressing one will unselect the other.
 */
export function LikeDislikeButtons({ value, onChange, disabled = false, size = IconButtonSize.Default, className, name }: LikeDislikeButtonsProps) {
  const handleLikeClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      onChange(true);
      // Remove focus from button after click
      event.currentTarget.blur();
    },
    [onChange]
  );

  const handleDislikeClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      onChange(false);
      // Remove focus from button after click
      event.currentTarget.blur();
    },
    [onChange]
  );

  // Choose the appropriate icons based on selection state
  const LikeIcon = value === true ? LikeSolid : Like;
  const DislikeIcon = value === false ? DislikeSolid : Dislike;

  // Determine the appropriate CSS classes based on state
  const getLikeButtonClass = () => {
    if (value === true) return styles.likeButtonActive;

    return styles.likeButtonDefault;
  };

  const getDislikeButtonClass = () => {
    if (value === false) return styles.dislikeButtonActive;

    return styles.dislikeButtonDefault;
  };

  return (
    <div aria-labelledby={`${name}-label`} className={`${styles.buttonGroup} ${className || ''}`} role="radiogroup">
      <IconButton
        aria-checked={value === true}
        aria-label="Correct"
        buttonSize={size}
        className={getLikeButtonClass()}
        disabled={disabled}
        icon={LikeIcon}
        role="radio"
        title="Correct"
        onClick={handleLikeClick}
      />
      <IconButton
        aria-checked={value === false}
        aria-label="Incorrect"
        buttonSize={size}
        className={getDislikeButtonClass()}
        disabled={disabled}
        icon={DislikeIcon}
        role="radio"
        title="Incorrect"
        onClick={handleDislikeClick}
      />
    </div>
  );
}
